<template>
  <div>
    <Modal :modalVisible="modal" @close="handleClose" width="600px" id="cancel-order-modal">
      <div slot="modal-content" class="text-left text-black">
        <!-- STEP HEADER -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <p class="text-3xl font-extrabold" v-if="step === 'reason'">Batalkan Pesanan?</p>
            <p class="text-3xl font-extrabold" v-else-if="step === 'terms'">Konfirmasi Pembatalan</p>
            <p class="text-3xl font-extrabold" v-else>Pesanan ini sedang dalam pengajuan pembatalan</p>
          </div>
        </div>

        <!-- STEP 1: REASONS -->
        <div v-if="step === 'reason'">
          <p class="text-xl font-bold mb-4">Alasan Pembatalan</p>
          <div class="flex flex-col items-start gap-4 text-black">
            <label v-for="(r, idx) in reasons" :key="idx" class="flex items-center gap-2">
              <input
                type="radio"
                name="cancelReason"
                :id="'reason' + idx"
                :value="r.value"
                v-model="selectedReason"
              />
              <span v-html="r.label"></span>
            </label>

            <div class="flex justify-center w-full" v-if="selectedReason === 'other'">
              <textarea
                name="otherReason"
                class="border border-gray-400 rounded-lg p-2"
                cols="60"
                rows="5"
                placeholder="Tulis alasan tambahan..."
                v-model="otherReason"
              ></textarea>
            </div>

            <div class="flex justify-center w-full gap-6 mt-4">
              <Button buttonText="Batal" type="error" @action="handleClose" size="full" />
              <Button
                buttonText="Lanjutkan Pembatalan"
                type="delete"
                @action="goToTerms"
                size="full"
                :disabled="!canProceedToTerms"
              />
            </div>
          </div>
        </div>

        <!-- STEP 2: TERMS & CONFIRM -->
        <div v-else-if="step === 'terms'">
          <p class="text-xl font-bold mb-4">Syarat & Ketentuan Pembatalan</p>

          <div class="text-sm text-neutral-700 leading-relaxed max-h-56 overflow-auto mb-4">
            <ol class="list-decimal list-outside ml-5">
              <li>Pengajuan pembatalan hanya bisa dilakukan dalam waktu maksimal 30 menit setelah pembayaran.</li>
              <li>Pembatalan akan diproses jika pihak ekspedisi menyetujui permintaan pembatalan.</li>
              <li>
                Pengembalian dana (refund) akan diproses sesuai metode pembayaran yang digunakan.
                <br/>Qris by GoPay (on-us) — Pembayaran melalui aplikasi gopay diproses 1 x 24 jam setelah Pengajuan.
                <br/>Qris by GoPay (off-us) — Pembayaran menggunakan e-wallet / bank lain diproses 15–20 hari kerja.
              </li>
            </ol>
          </div>

          <hr class="mb-4 mt-4">
          <div>
            <label class="inline-flex items-center gap-4">
              <input type="checkbox" v-model="acceptedTerms" />
              <span>Saya telah membaca dan setuju dengan syarat & ketentuan pembatalan pesanan ini.</span>
            </label>
            <div class="text-sm text-muted mt-4 mb-2 text-gray-400">
              Dengan melanjutkan, pembatalan akan diproses sesuai kebijakan yang tercantum di atas.
            </div>
          </div>

          <div class="flex justify-center w-full gap-4 mt-6">
            <Button buttonText="Kembali" type="secondary" @action="backToReasons" size="full" />
            <Button
              buttonText="Batalkan Pesanan"
              type="delete"
              @action="confirmCancel"
              :disabled="!acceptedTerms || loading"
              size="full"
            />
          </div>
        </div>
        
         <div v-else-if="step === 'success'">
          <p class="text-justify">Mohon tunggu hinga proses verifikasi selesai. Anda akan menerima notifikasi setelah
            pembatalan dikonfirmasi
          </p>
        </div>

        <!-- LOADING -->
        <div v-else-if="step === 'loading'" class="text-center py-12">
          <p class="mb-2">Memproses pembatalan...</p>
        </div>

      </div>
    </Modal>

  </div>
</template>

<script>
import Modal from '@/components/Modal/Modal'
import Button from '@/components/Forms/Button'

export default {
  components: {
    Modal: () => import(/* webpackChunkName: "Modal" */ '@/components/Modal/Modal'),
    Button: () => import('@/components/Forms/Button'),
  },
  props: {
    modal: { type: Boolean, required: true } // kontrol dari parent
  },
  data() {
    return {
      // step: 'reason' | 'terms' | 'loading'
      step: 'reason',
      reasons: [
        { value: 'change_courier', label: 'Ingin mengganti jasa pengiriman' },
        { value: 'wrong_detail', label: 'Ada kesalahan dalam detail pengiriman (alamat/nama/telepon)' },
        { value: 'change_order', label: 'Ingin ubah detail pesanan (berat, jenis paket, dll.)' },
        { value: 'wrong_service', label: 'Salah input jenis layanan (Regular/Sameday/Nextday/Cargo)' },
        { value: 'change_payment', label: 'Ingin mengganti metode pembayaran' },
        { value: 'other', label: 'Alasan lainnya' }
      ],
      selectedReason: '',
      otherReason: '',
      acceptedTerms: false,
      loading: false
    }
  },
  computed: {
    // derived final reason string to submit
    cancelReason() {
      if (this.selectedReason === 'other') {
        return this.otherReason?.trim() || ''
      }
      const found = this.reasons.find(r => r.value === this.selectedReason)
      return found ? found.label : ''
    },
    canProceedToTerms() {
      // must select a reason; if 'other' must also fill otherReason
      if (!this.selectedReason) return false
      if (this.selectedReason === 'other') {
        return this.otherReason && this.otherReason.trim().length > 0
      }
      return true
    }
  },
  watch: {
    // reset internal when parent toggles modal closed from outside
    modal(val) {
      if (!val) this.resetState()
    }
  },
  methods: {
    handleClose() {
      // close modal (inform parent)
      this.$emit('toggle')
      const currentStep = this.step
      if (currentStep === 'success') {
        setTimeout(() => {
          window.location.reload()
        }, 250)
      } else {
        this.resetState()
      }

    },
    goToTerms() {
      if (!this.canProceedToTerms) return
      this.step = 'terms'
      // reset acceptance when entering terms
      this.acceptedTerms = false
    },
    backToReasons() {
      this.step = 'reason'
    },
    async confirmCancel() {
      if (!this.acceptedTerms) return
      // set loading state
      this.loading = true
      this.step = 'loading'

      const orderId = this.$store.getters['order/orderDetail'].order_id
      const reason = this.cancelReason

      try {
        await this.$store.dispatch('order/cancelOrder', {
          orderId,
          reason
        })

        // success flow
        this.loading = false;
        this.step = 'success';
        
      } catch (err) {
        console.error('Pembatalan gagal:', err)
        this.loading = false
        this.$emit('closeAll')
        this.resetState(false)
        this.$store.dispatch('common/setToast', {
          options: {
            type: 'error',
            duration: 3000,
            primaryText: 'Pembatalan Gagal',
            secondaryText: 'Terjadi kesalahan saat membatalkan pesanan. Silakan coba lagi.'
          }
        })
      }
    },
    resetState(emitClose = true) {
      this.step = 'reason'
      this.selectedReason = ''
      this.otherReason = ''
      this.acceptedTerms = false
      this.loading = false
      if (emitClose) {
        // keep previous behavior: parent might expect toggle() to be called for closing
        // but in places where we call closeAll(), parent will handle it
        // do not emit closeAll here automatically
      }
    }
  }
}
</script>
